import {QuestionStatus, questionStatusMd} from './questionStatus'
import {WorkspaceTypes as W} from './workspaceTypes'
import {pipe, filter, map, findIndex, concat, sort, sortBy} from 'remeda'
import {DateTime} from 'luxon'
import {doSafe} from '../promise'

export type QuestionForSort = {
  id: string
  cfg: {status: QuestionStatus; title: string}
  created_at: string
  forecasts_to?: DateTime
}

export const questionSortTimestamp = (q: Pick<QuestionForSort, 'forecasts_to' | 'created_at'>) =>
  (q.forecasts_to ?? DateTime.fromISO(q.created_at)).toMillis()

/** the questionOrder is from when the admin last re-ordered. Derive on that respects the original ordering and slots the existing questions as best we can into it */
export const questionOrderForLayout = (questions: QuestionForSort[], questionOrder: W.QuestionOrder) => {
  const processQuestions = (questions: QuestionForSort[], existingOrder: string[]) => {
    const newQuestions = pipe(
      questions,
      filter(q => findIndex(existingOrder, id => id == q.id) == -1),
      sort((a, b) => questionSortTimestamp(a) - questionSortTimestamp(b))
    )

    const allWithTimestamps = concat(
      existingOrder.map(id => ({
        id,
        orderTimestamp:
          doSafe(
            questions.find(q => q.id == id),
            questionSortTimestamp
          ) ?? 0
      })),
      newQuestions.map(q => ({id: q.id, orderTimestamp: questionSortTimestamp(q)}))
    )

    return pipe(
      allWithTimestamps,
      sort((a, b) => a.orderTimestamp - b.orderTimestamp),
      map(item => item.id)
    )
  }

  return {
    active: processQuestions(
      filter(questions, q => questionStatusMd[q.cfg.status].active == true),
      questionOrder.active
    ),
    inactive: processQuestions(
      filter(questions, q => questionStatusMd[q.cfg.status].active != true),
      questionOrder.inactive
    ),
    getOrder: (question: QuestionForSort) => {
      const listForStatus = questionStatusMd[question.cfg.status].active ? 'active' : 'inactive'
      return questionOrder[listForStatus].indexOf(question.id)
    }
  }
}

export const sortQuestions = <T extends QuestionForSort>(questions: T[], questionOrder: W.QuestionOrder) => {
  const order = questionOrderForLayout(questions, questionOrder)
  return sortBy(questions, q => order.getOrder(q) ?? Number.MAX_SAFE_INTEGER, [q => questionSortTimestamp(q), 'desc'])
}
